import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Globe, Smartphone, Monitor, Tablet, Bot, CheckCircle2, XCircle, LogIn, Shield, Eye, Server, Trash2, Eraser } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/pages/admin/AdminAuth";
import { PageTitle, Panel, Label } from "@/pages/admin/AdminLayout";

const COUNTRY_LABEL = { PT: "Portugal", ES: "Espanha", FR: "França", DE: "Alemanha", GB: "Reino Unido", US: "Estados Unidos", BR: "Brasil", IT: "Itália", NL: "Países Baixos", CH: "Suíça", BE: "Bélgica", LU: "Luxemburgo", IE: "Irlanda", AT: "Áustria", SE: "Suécia", NO: "Noruega", DK: "Dinamarca", FI: "Finlândia", PL: "Polónia", RO: "Roménia", CZ: "Chéquia", GR: "Grécia", AO: "Angola", MZ: "Moçambique", CV: "Cabo Verde", AE: "Emirados Árabes Unidos", SA: "Arábia Saudita", CA: "Canadá", MX: "México", JP: "Japão", HK: "Hong Kong", SG: "Singapura", CN: "China", AU: "Austrália" };
const flag = (cc) => cc && /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0))) : "🌐";
const label = (cc) => COUNTRY_LABEL[cc] || cc || "Desconhecido";

const DEVICE_ICON = { mobile: Smartphone, desktop: Monitor, tablet: Tablet, bot: Bot };
const SCOPES = [
  { k: "page", t: "Visitas ao site", icon: Eye },
  { k: "login", t: "Logins no backoffice", icon: LogIn },
  { k: "2fa", t: "Verificações 2FA", icon: Shield },
  { k: "api", t: "API pública", icon: Server },
];

function Bar({ data, max }) {
  if (!data?.length) return <p className="text-xs text-[#A39E93] py-2">Sem dados.</p>;
  return (
    <div className="space-y-1.5">
      {data.map((r) => (
        <div key={r.k} className="relative">
          <div className="h-6 bg-[#C8AE6E]/15 border border-[#C8AE6E]/25" style={{ width: `${Math.max(4, (r.n / max) * 100)}%` }} />
          <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
            <span className="text-[#F3EFE6] truncate pr-2">{r.k}</span>
            <span className="text-[#E6D5AC] tabular-nums">{r.n}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AccessLog() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [scope, setScope] = useState("page");
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => { setPage(1); }, [scope, days]);
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/access/summary", { params: { scope, days } }),
      api.get("/admin/access/list", { params: { scope, days, page, page_size: 100 } }),
    ]).then(([s, l]) => { setSummary(s.data); setList(l.data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [scope, days, page, refresh]);
  const remove = async (id) => {
    if (!window.confirm("Eliminar esta entrada?")) return;
    try { await api.delete(`/admin/access/${id}`); setList((l) => ({ ...l, items: l.items.filter((x) => x.id !== id), total: Math.max(0, l.total - 1) })); toast.success("Entrada eliminada"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const purge = async (opts) => {
    const label = opts.only_bots ? "entradas de bots" : opts.older_than_days ? `entradas com mais de ${opts.older_than_days} dias` : `todas as entradas de "${scope}" dos últimos ${days} dias`;
    if (!window.confirm(`Eliminar ${label}? Esta ação é irreversível.`)) return;
    try {
      const { data } = await api.post("/admin/access/purge", { scope, older_than_days: opts.older_than_days || 0, only_bots: !!opts.only_bots });
      toast.success(`${data.deleted} entradas eliminadas`);
      setRefresh((r) => r + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const countries = useMemo(() => (summary?.countries || []).map((r) => ({ ...r, k: `${flag(r.k === "—" ? null : r.k)}  ${label(r.k === "—" ? null : r.k)}` })), [summary]);
  const max = (arr) => Math.max(1, ...(arr || []).map((r) => r.n));
  const nicePath = (p) => (p || "").replace(/^\//, "").slice(0, 60) || "/";

  const sub = isSuper
    ? "Visitas, logins e verificações 2FA. IP mascarado (primeiro e último octeto). Vê também os seus próprios eventos de super administrador."
    : "Visitas, logins e verificações 2FA. IP mascarado (primeiro e último octeto). Os eventos do super administrador ficam ocultos.";

  return (
    <div data-testid="access-log-page">
      <PageTitle title="Registo de acessos" sub={sub}>
        <select className="afield !py-2" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="access-days">
          <option value={1}>Últimas 24h</option>
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </select>
      </PageTitle>

      <div className="flex flex-wrap gap-2 mb-5">
        {SCOPES.map(({ k, t, icon: Ic }) => (
          <button key={k} type="button" onClick={() => setScope(k)} className={`px-3 py-2 text-xs flex items-center gap-2 border ${scope === k ? "bg-[#C8AE6E] text-[#0C0B09] border-[#C8AE6E]" : "border-[#282520] text-[#A39E93] hover:text-[#E6D5AC]"}`} data-testid={`access-scope-${k}`}>
            <Ic className="w-3.5 h-3.5" /> {t}
          </button>
        ))}
      </div>

      {summary && (
        <div className="grid lg:grid-cols-4 gap-4 mb-6" data-testid="access-summary">
          <Panel><Label>Total · {summary.days} dias</Label><p className="text-3xl text-[#F3EFE6] tabular-nums">{summary.total}</p>
            {(scope === "login" || scope === "2fa") && (
              <p className="text-xs mt-2"><span className="text-[#9fc79a] flex items-center gap-1 inline-flex"><CheckCircle2 className="w-3 h-3" /> {summary.success || 0}</span> <span className="text-[#e0937f] flex items-center gap-1 inline-flex ml-3"><XCircle className="w-3 h-3" /> {summary.fail || 0}</span></p>
            )}
          </Panel>
          <Panel><Label>Países</Label><Bar data={countries.slice(0, 8)} max={max(summary.countries)} /></Panel>
          <Panel><Label>Browsers</Label><Bar data={summary.browsers.slice(0, 8)} max={max(summary.browsers)} /></Panel>
          <Panel><Label>Dispositivos / Sistema</Label>
            <div className="flex flex-wrap gap-3 text-sm text-[#F3EFE6] mb-3">
              {summary.devices.map((r) => { const Ic = DEVICE_ICON[r.k] || Monitor; return <span key={r.k} className="flex items-center gap-1.5 bg-[#C8AE6E]/10 border border-[#C8AE6E]/25 px-2.5 py-1 text-xs"><Ic className="w-3.5 h-3.5 text-[#C8AE6E]" /> {r.k} · <span className="text-[#E6D5AC] tabular-nums">{r.n}</span></span>; })}
            </div>
            <Bar data={summary.os.slice(0, 6)} max={max(summary.os)} />
          </Panel>
        </div>
      )}

      {summary?.top_paths?.length > 0 && (
        <Panel className="mb-6"><Label>Páginas mais vistas</Label><Bar data={summary.top_paths.slice(0, 15).map((r) => ({ ...r, k: nicePath(r.k) }))} max={max(summary.top_paths)} /></Panel>
      )}

      <Panel>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-lg">Registo detalhado</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-xs text-[#A39E93]">{list.total} entradas · pág. {page} de {Math.max(1, Math.ceil(list.total / 100))}</p>
            {isSuper && <>
              <button type="button" onClick={() => purge({ only_bots: true })} className="btn-line !py-1.5 !px-3 text-xs" data-testid="access-purge-bots"><Bot className="w-3.5 h-3.5" /> Limpar bots</button>
              <button type="button" onClick={() => purge({ older_than_days: 30 })} className="btn-line !py-1.5 !px-3 text-xs" data-testid="access-purge-30"><Eraser className="w-3.5 h-3.5" /> {">"} 30 dias</button>
              <button type="button" onClick={() => purge({})} className="btn-line !py-1.5 !px-3 text-xs !border-[#e0937f]/40 !text-[#e0937f]" data-testid="access-purge-all"><Trash2 className="w-3.5 h-3.5" /> Limpar âmbito</button>
            </>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-[0.2em] text-[#A39E93]"><tr>
              <th className="text-left py-2 pr-3">Data</th>
              <th className="text-left pr-3">País</th>
              <th className="text-left pr-3">IP</th>
              <th className="text-left pr-3">Browser · SO</th>
              <th className="text-left pr-3">Disp.</th>
              {(scope === "login" || scope === "2fa") ? <>
                <th className="text-left pr-3">Email tentado</th>
                <th className="text-left pr-3">Resultado</th>
              </> : <th className="text-left pr-3">Caminho</th>}
              {isSuper && <th className="text-right pr-1 w-10"></th>}
            </tr></thead>
            <tbody className="text-[#F3EFE6]">
              {list.items.map((it) => {
                const Ic = DEVICE_ICON[it.device] || Monitor;
                return (
                  <tr key={it.id} className={`border-t border-[#1f1d19] ${it.is_super ? "bg-[#C8AE6E]/5" : ""}`} data-testid={`access-row-${it.id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap text-[#A39E93] tabular-nums">
                      {new Date(it.ts).toLocaleString("pt-PT")}
                      {it.is_super && <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-[#C8AE6E]/20 text-[#E6D5AC] border border-[#C8AE6E]/40" title="Evento do super administrador (apenas visível para si)">Super</span>}
                    </td>
                    <td className="pr-3 whitespace-nowrap">{flag(it.country)} {label(it.country)}</td>
                    <td className="pr-3 whitespace-nowrap font-mono text-[#A39E93]">{it.ip_masked || "—"}</td>
                    <td className="pr-3 whitespace-nowrap">{it.browser} · <span className="text-[#A39E93]">{it.os}</span></td>
                    <td className="pr-3"><span className="inline-flex items-center gap-1 text-[#E6D5AC]"><Ic className="w-3.5 h-3.5" /> {it.device}</span></td>
                    {(scope === "login" || scope === "2fa") ? <>
                      <td className="pr-3 text-[#E6D5AC]">{it.email || "—"}</td>
                      <td className="pr-3">{it.success ? <span className="text-[#9fc79a] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> ok</span> : <span className="text-[#e0937f] flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {it.reason}</span>}</td>
                    </> : <td className="pr-3 text-[#A39E93]">{it.path || "—"}</td>}
                    {isSuper && <td className="pr-1 text-right"><button type="button" onClick={() => remove(it.id)} className="p-1 text-[#A39E93] hover:text-[#e0937f]" title="Eliminar entrada" data-testid={`access-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>}
                  </tr>
                );
              })}
              {!list.items.length && !loading && <tr><td colSpan={isSuper ? 8 : 7} className="py-8 text-center text-[#A39E93]">Sem registos para este âmbito e período.</td></tr>}
            </tbody>
          </table>
        </div>
        {list.total > 100 && (
          <div className="flex justify-center gap-2 mt-4">
            <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} className="btn-line text-xs !py-1.5 disabled:opacity-30" data-testid="access-prev">Anterior</button>
            <button type="button" disabled={page * 100 >= list.total} onClick={() => setPage(page + 1)} className="btn-line text-xs !py-1.5 disabled:opacity-30" data-testid="access-next">Seguinte</button>
          </div>
        )}
      </Panel>
    </div>
  );
}
